import useBrainSearchBot from "@/admin/integrations/chat-bot/util/useBrainSearchBot"
import { PastUserBrainSearchesContentUsageFragment$key } from "@/brain-search/internal/__generated__/PastUserBrainSearchesContentUsageFragment.graphql"
import { PastUserBrainSearchesContentUsageFragmentRefetchQuery } from "@/brain-search/internal/__generated__/PastUserBrainSearchesContentUsageFragmentRefetchQuery.graphql"
import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import Relay from "@/relay/relayUtils"
import useUserTimezone from "@/user/util/useUserTimezone"
import { formatDateWithOptions } from "@/utils/time/timeUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoIcon, DiscoText } from "@disco-ui"
import DiscoContainerButton from "@disco-ui/button/DiscoContainerButton"
import { DATE_FORMAT } from "@utils/time/timeConstants"
import { TestIDProps } from "@utils/typeUtils"
import pluralize from "pluralize"
import {
  graphql,
  useRefetchableFragment,
  useSubscribeToInvalidationState,
} from "react-relay"

interface PastUserBrainSearchesProps extends TestIDProps {
  contentUsageKey?: PastUserBrainSearchesContentUsageFragment$key | null
}

function PastUserBrainSearches(props: PastUserBrainSearchesProps) {
  const { testid = "PastUserBrainSearches", contentUsageKey } = props
  const bot = useBrainSearchBot()

  const classes = useStyles()
  const drawer = useGlobalDrawer("brainSearch")
  const userTimeZone = useUserTimezone()

  const [contentUsage, refetch] = useRefetchableFragment<
    PastUserBrainSearchesContentUsageFragmentRefetchQuery,
    PastUserBrainSearchesContentUsageFragment$key
  >(
    graphql`
      fragment PastUserBrainSearchesContentUsageFragment on ContentUsage
      @refetchable(queryName: "PastUserBrainSearchesContentUsageFragmentRefetchQuery")
      @argumentDefinitions(
        last: { type: "Int", defaultValue: 3 }
        before: { type: "String" }
      ) {
        id
        viewerBrainSearches(last: $last, before: $before)
          @connection(key: "PastUserBrainSearches__viewerBrainSearches") {
          __id
          edges {
            node {
              id
              title
              createdAt
            }
          }
        }
      }
    `,
    contentUsageKey || null
  )

  useSubscribeToInvalidationState(
    contentUsage?.viewerBrainSearches?.__id
      ? [contentUsage.viewerBrainSearches.__id]
      : [],
    () => {
      if (contentUsageKey) {
        refetch({ last: 3 }, { fetchPolicy: "store-and-network" })
      }
    }
  )

  const brainSearches = Relay.connectionToArray(contentUsage?.viewerBrainSearches)

  const handleConversationClick = (conversationId: string) => {
    drawer.setParams({ convId: conversationId })
  }

  if (!brainSearches || brainSearches.length === 0 || !bot) {
    return null
  }

  return (
    <div className={classes.wrapper} data-testid={testid}>
      <div className={classes.header}>
        <div className={classes.leftTitleGroup}>
          <div className={classes.iconContainer}>
            <DiscoIcon icon={"time"} width={20} height={20} active />
          </div>
          <div className={classes.textColumn}>
            <DiscoText variant={"body-sm-700"}>
              {`Your ${pluralize("previous conversation", brainSearches.length)} with ${
                bot.name
              }`}
            </DiscoText>
            <DiscoText variant={"body-xs"} color={"text.secondary"}>
              {`${brainSearches.length} most recent ${pluralize(
                "conversation",
                brainSearches.length
              )}`}
            </DiscoText>
          </div>
        </div>
      </div>

      <div className={classes.conversationsBoxContent}>
        {brainSearches.map((conversation) => (
          <div key={conversation.id} className={classes.conversationWrapper}>
            <DiscoContainerButton
              className={classes.conversationItem}
              onClick={() => handleConversationClick(conversation.id)}
            >
              <div className={classes.conversationContent}>
                <DiscoText
                  variant={"body-sm-500"}
                  className={classes.conversationTitle}
                  truncateText={2}
                >
                  {conversation.title}
                </DiscoText>
                <div className={classes.conversationMeta}>
                  <DiscoText variant={"body-xs"} color={"text.secondary"}>
                    {formatDateWithOptions({
                      timeZone: userTimeZone,
                      format: DATE_FORMAT.DEFAULT,
                    })(new Date(conversation.createdAt))}
                  </DiscoText>
                </div>
              </div>
              <DiscoIcon icon={"arrow-stem-right"} className={classes.chevronIcon} />
            </DiscoContainerButton>
          </div>
        ))}
      </div>
    </div>
  )
}

const useStyles = makeUseStyles((theme) => ({
  wrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    alignSelf: "stretch",
    width: "100%",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.measure.borderRadius.large,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.palette.groovyDepths.xs,
    margin: theme.spacing(1, 0),
  },
  header: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: theme.spacing(1.5, 2),
  },
  leftTitleGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  textColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(0.25),
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    borderRadius: theme.measure.borderRadius.medium,
    backgroundColor: theme.palette.groovy.neutral[300],
  },
  conversationsBoxContent: {
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    maxHeight: "300px",
    overflowY: "auto",
  },
  conversationWrapper: {
    width: "100%",
    padding: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  conversationItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: theme.spacing(1.5),
    borderRadius: theme.measure.borderRadius.medium,
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  conversationContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    flex: 1,
    minWidth: 0,
  },
  conversationTitle: {
    color: theme.palette.text.primary,
    lineHeight: 1.4,
  },
  conversationMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  chevronIcon: {
    display: "none",
    color: theme.palette.text.secondary,
    width: "16px",
    height: "16px",
    flexShrink: 0,
    opacity: 0.6,
    "$conversationItem:hover &": {
      display: "inline-flex",
    },
  },
}))

export default PastUserBrainSearches
